import { useEffect, useMemo, useState } from 'react';

import { GAP_TYPE_ORDER, GAP_TYPE_META } from '../gap-meta';
import { useGaps } from '../state/gaps';
import type { Gap, GapType } from '../types';
import { GEO_ALL } from '../types';

import { GapCard } from './GapCard';
import { GapComposer } from './GapComposer';

/**
 * Right-rail panel for a single form.
 *
 * Shows gaps filtered to this KB number + top-bar geography, grouped by type
 * in the fixed project order (new_form_required is hidden in this context —
 * it's stream-scoped only).
 *
 * `refreshTick` is bumped by the parent (FormScreen) when a gap is saved
 * outside the panel — e.g. via the top-bar "Add gap" button — so the panel
 * re-fetches without the parent reaching into our internal state.
 */
export function GapsPanel({
  stream,
  kbNumber,
  formSysId,
  formName,
  topBarGeo,
  refreshTick = 0,
}: {
  stream: string;
  kbNumber: string;
  formSysId: string | null;
  formName: string | null;
  topBarGeo: string;
  refreshTick?: number;
}) {
  const filters = useMemo(
    () => ({
      kb_number: kbNumber,
      geography: topBarGeo === GEO_ALL ? undefined : topBarGeo,
    }),
    [kbNumber, topBarGeo],
  );
  const { gaps, loading, error, refresh } = useGaps(filters);
  const [composing, setComposing] = useState(false);

  // External refresh signal from the parent (e.g., top-bar gap composer save).
  useEffect(() => {
    if (refreshTick > 0) refresh();
    // refresh is stable from useGaps; we deliberately depend on the tick only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const grouped = useMemo(() => groupByType(gaps), [gaps]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">
          Gaps on this form
          <span className="ml-1.5 text-slate-400 font-normal">({gaps.length})</span>
        </h3>
        {!composing && (
          <button className="btn-secondary h-8" onClick={() => setComposing(true)}>
            + Add gap
          </button>
        )}
      </div>

      {composing && (
        <GapComposer
          stream={stream}
          kbNumber={kbNumber}
          formSysId={formSysId}
          formName={formName}
          topBarGeo={topBarGeo}
          onCancel={() => setComposing(false)}
          onSaved={() => {
            setComposing(false);
            refresh();
          }}
        />
      )}

      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {!loading && gaps.length === 0 && !composing && (
        <div className="text-sm text-slate-500 italic">
          No gaps logged
          {topBarGeo !== GEO_ALL ? ' for the selected geography yet.' : ' yet.'}
        </div>
      )}

      {GAP_TYPE_ORDER.filter((t) => t !== 'new_form_required').map((type) => {
        const list = grouped.get(type);
        if (!list || list.length === 0) return null;
        return (
          <GapGroup key={type} type={type} gaps={list} onChanged={refresh} />
        );
      })}
    </div>
  );
}

function GapGroup({
  type,
  gaps,
  onChanged,
}: {
  type: GapType;
  gaps: Gap[];
  onChanged: () => void;
}) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {GAP_TYPE_META[type].label}
        <span className="ml-1.5 text-slate-400 font-normal normal-case tracking-normal">
          ({gaps.length})
        </span>
      </h4>
      {gaps.map((g) => (
        <GapCard key={g.id} gap={g} onChanged={onChanged} />
      ))}
    </section>
  );
}

function groupByType(gaps: Gap[]): Map<GapType, Gap[]> {
  const m = new Map<GapType, Gap[]>();
  for (const g of gaps) {
    const arr = m.get(g.gap_type) ?? [];
    arr.push(g);
    m.set(g.gap_type, arr);
  }
  return m;
}
